/*
 * ORDERS — the customer's order list (profile), for every theme.
 *
 * Data and rules are here; the theme gives the markup (the in-DOM template of
 * #orders) and the texts:
 *   <main id="orders" data-texts="#json-script-id[, #another]" data-page-size="5">
 * For each order the template can use card(order) → {status, items, primary,
 * actions, dates, date} (status: tone, icon, label, note, step, stopped, pay,
 * ret), the list: visibleCards, toneCounts, hasMore, query, tone, showMore(),
 * and the purchased products: collection (+ collFilter, collSel, collBack),
 * reorder(entry), openProduct(item).
 * The order actions are OrderRules' (the same as the order manage dialog).
 */
const ORDER_PAGE = 5;

const Orders = {
    data() {
        const el = document.getElementById('orders');
        // data-texts: one or more JSON scripts (comma separated selectors), merged
        const texts = {};
        String((el && el.dataset.texts) || '').split(',').map((x) => x.trim()).filter(Boolean).forEach((sel) => {
            try {
                const src = document.querySelector(sel);
                if (src) Object.assign(texts, JSON.parse(src.textContent));
            } catch (e) { /* a broken text block is skipped */ }
        });
        const page = Number(el && el.dataset.pageSize) || ORDER_PAGE;
        return {
            lan: window.lan || document.documentElement.lang || 'hu',
            orders: null,
            ordersError: null,
            // the backend sends the orders in pages (simplePaginate): the next one to load, if any
            nextPage: null,
            loadingMore: false,
            texts,
            page,
            // the list: search, status filter, how many are shown
            query: '',
            tone: 'all',
            limit: page,
            // purchased products: colour filter, the open entry, returned ones shown
            collFilter: 'all',
            collSel: null,
            collBack: false,
        };
    },
    computed: {
        cards() {
            return ((this.orders && this.orders.data) || []).map((order) => this.card(order));
        },
        // filter keys: work (in progress), ok (done), ret (return / refund), off (cancelled, failed)
        filteredCards() {
            const q = String(this.query || '').trim().toLowerCase();
            return this.cards.filter((c) => (this.tone === 'all' || this.tone === c.filter)
                && (!q || c.haystack.indexOf(q) !== -1));
        },
        visibleCards() { return this.filteredCards.slice(0, this.limit); },
        hasMore() { return this.filteredCards.length > this.limit || !!this.nextPage; },
        toneCounts() {
            const n = { all: this.cards.length, work: 0, ok: 0, ret: 0, off: 0 };
            this.cards.forEach((c) => { n[c.filter] += 1; });
            return n;
        },
        // no search / filter bar over a single order, no filter row when all are alike
        showBar() { return this.cards.length > 1; },
        showFilters() { return Object.keys(this.toneCounts).filter((k) => k !== 'all' && this.toneCounts[k] > 0).length > 1; },
        collection() { return this.buildCollection((this.orders && this.orders.data) || []); },
    },
    watch: {
        query() { this.limit = this.page; },
        tone() { this.limit = this.page; },
    },
    mounted(){
        this.fetchOrders();
    },
    methods:{
        t(key, fallback) { return this.texts[key] ?? fallback ?? ''; },
        // the next batch; past what is loaded, the next backend page comes (repeated while a filter finds too few)
        async showMore() {
            this.limit += this.page;
            while (this.nextPage && this.filteredCards.length < this.limit && !this.loadingMore) {
                if (!(await this.fetchOrders(this.nextPage))) break;
            }
        },
        setTone(tone) { this.tone = tone; },

        /* ── one order card ─────────────────────────────────────────── */
        card(order) {
            const status = this.status(order);
            const items = this.items(order);
            let actions = this.actions(order);
            let primary = this.primary(order, status, actions);
            // an order of another sale channel can only be managed there: what would open the dialog opens that channel's profile
            const other = order.other_channel;
            if (other && other.url) {
                actions = actions.map((a) => (a.href ? a : { ...a, href: other.url }));
                if (primary && !primary.href) primary = { key: primary.key, href: other.url, label: primary.label, dark: primary.dark };
            }
            return {
                order,
                status,
                items,
                actions,
                primary,
                dates: this.dates(order),
                date: this.date(order),
                filter: status.tone === 'done' ? 'ok' : status.tone === 'off' ? 'off' : status.tone === 'ret' ? 'ret' : 'work',
                // what the search looks in: order number and product names
                haystack: [order.pretty_id].concat(items.map((it) => it.name)).join(' ').toLowerCase(),
            };
        },
        // label, tone (work | done | ret | off), icon (pack | ship | done | ret | off), note, step 1-4, return progress
        status(order) {
            const t = this.texts, L = (window.lang && lang.statuses) || {};
            const s = OrderRules.state(order);
            const ps = Number(order.payment_status);
            let out;
            if (s.kind === 'refused') out = { tone: 'off', label: t.refused || L.refused, note: t.note_refused };
            else if (s.kind === 'denied') out = { tone: 'off', label: t.denied || L.deleted || t.cancelled, note: t.note_denied || t.note_cancelled };
            else if (s.kind === 'failed') out = { tone: 'off', label: t.pay_failed || L.payment_failed, note: t.note_failed };
            else if (s.kind === 'cancelled') out = { tone: 'off', label: L.deleted || t.cancelled, note: t.note_cancelled };
            else if (s.kind === 'unfinished') out = { tone: 'work', label: L.not_finished || t.not_finished, note: t.note_unpaid };
            // a delayed exchange: the new parcel goes out after the returned one is back (calculated by the backend)
            else if ((order.orderDeliveries || []).some((d) => d.waiting_for_return)) out = { tone: 'work', label: t.awaiting_return, note: t.note_awaiting_return };
            else if (s.kind === 'refund') {
                const pending = ps === 6 || ps === 8, done = ps === 4 || ps === 5;
                // the return parcel: the later delivery record (the first is the outbound one)
                const dels = (order.orderDeliveries || []).map((d) => ({ d, ts: this.parse(d.tracking_code_created_at) }))
                    .sort((a, b) => (a.ts ? a.ts.getTime() : 0) - (b.ts ? b.ts.getTime() : 0));
                const back = dels.length > 1 ? dels[dels.length - 1] : null;
                let rstep = 1;
                if (back && back.d.public_tracking_code) rstep = 2;
                // the warehouse received it (the backend stamps the return delivery as delivered): arrived, being checked
                if (back && back.d.delivered_at) rstep = 3;
                if (done) rstep = 4;
                const sum = (order.orderItems || []).filter((it) => Number(it.quantity) <= 0).reduce((a, it) => a + (Number(it.payed_price) || 0), 0);
                const amount = sum ? this.money({ amount: sum, currency: this.currencyOf(order) }) : '';
                out = {
                    tone: 'ret',
                    label: pending ? t.refund_pending : (ps === 5 ? t.refund_partial : (L.refunded || t.refunded)),
                    note: pending ? t.note_refund : '',
                    ret: {
                        step: rstep,
                        dates: [back && back.ts ? this.fmt(back.ts) : '', '', '', ''],
                        amount: amount ? String(done ? (ps === 5 ? t.r_amount_part : t.r_amount_done) : t.r_amount_wait).replace(':price', amount) : '',
                    },
                };
            } else if (s.kind === 'changed') out = { tone: 'ret', label: L.changed || t.changed, note: '' };
            else if (s.kind === 'delivered') {
                const days = (order.webshop && order.webshop.return_allowed_days) || '';
                const swap = this.channel().isChange;
                const note = OrderRules.returnWindow(order) === 'open' && days ? String(swap ? t.note_return_swap : t.note_return).replace(':days', days) : '';
                out = { tone: 'done', label: L.delivered || t.delivered, note };
            } else {
                const method = order.last_delivery_method_name || '';
                if (s.kind === 'pickup') out = { tone: 'work', label: t.pickup, note: (t.note_pickup || '') + (method ? ' · ' + method : '') };
                else if (s.kind === 'shipping') out = { tone: 'work', label: t.shipping, note: (t.note_shipping || '') + (method ? ' · ' + method : '') };
                else out = { tone: 'work', label: s.kind === 'packing' ? t.packing : (L.under_process || t.processing), note: OrderRules.canModify(order) ? t.note_modify : t.note_packing };
            }
            out.stopped = s.stopped; out.pay = s.pay; out.step = s.step; out.ret = out.ret || null;
            out.icon = s.stopped ? 'off' : out.tone === 'ret' ? 'ret' : out.tone === 'done' ? 'done'
                : (s.step >= 3 || Number(order.delivered_status) === 8) ? 'ship' : 'pack';
            return out;
        },
        channel() {
            const el = document.querySelector('[data-order-manage-app]');
            return { isChange: !!el && el.dataset.isChange === '1', isReturn: !!el && el.dataset.isReturn === '1' };
        },
        // the labelled actions (the same list as the order manage dialog's menu)
        actions(order) {
            if (window.OrderManage) return OrderManage.actions(order);
            return OrderRules.actions(order, this.channel()).map((a) => ({ ...a, label: this.t('m_' + a.key, a.key), sub: a.detail || this.t('m_' + a.key + '_s') }));
        },
        // the card's main button: pay · return (in the window; the late return stays in the list) · track · manage
        primary(order, status, actions) {
            status = status || this.status(order);
            if (status.stopped && !status.pay) return null;
            actions = actions || this.actions(order);
            const find = (k) => actions.find((a) => a.key === k);
            if (status.pay && find('pay')) return { key: 'pay', href: find('pay').href, label: find('pay').label, dark: true };
            const ret = find('return') || find('return_change');
            if (status.step === 4 && OrderRules.returnWindow(order) === 'open' && ret) return { key: ret.key, icon: 'return', flow: 'return', label: ret.label, dark: true };
            if (find('track') && status.step < 4) return { key: 'track', href: find('track').href, blank: true, label: find('track').label, dark: true };
            if (find('address') || find('point')) return { key: 'menu', flow: 'menu', label: this.t('m_manage', 'Manage'), dark: false };
            return null;
        },
        currencyOf(order) { return (order.price && order.price.currency) || order.currency_code || ''; },
        money(p) {
            if (!p || p.amount === null || p.amount === undefined) return '';
            return Money.format(p.amount, p.currency || 'HUF');
        },
        // ALL-CAPS characteristic values read better capitalised ("BLACK" → "Black")
        nice(v) { v = String(v || ''); return /^[A-Z\s-]+$/.test(v) && v.length > 3 ? v.charAt(0) + v.slice(1).toLowerCase() : v; },
        // the item's variant characteristics by their settings (is_variant, main_characteristics)
        chars(it) {
            const pv = it.productVariant || {};
            const orig = (it.other_variants || []).find((v) => v.is_original) || (it.other_variants || []).find((v) => v.product_variant_id === it.product_variant_id);
            const list = (pv.characteristics && pv.characteristics.length) ? pv.characteristics : ((orig && orig.characteristics) || []);
            return {
                main: list.find((c) => c.main_characteristics) || null,
                other: list.find((c) => c.is_variant && !c.main_characteristics) || null,
                orig,
            };
        },
        itemName(it) {
            const pv = it.productVariant || {};
            const tr = (pv.product && pv.product.product_translations) || [];
            return (tr.find((x) => x.language_code === this.lan) || tr[0] || {}).name || '';
        },
        // the items on the card; a returned item stays in the order with quantity 0
        items(order) {
            const t = this.texts;
            const list = order.orderItems || [];
            const live = list.filter((it) => Number(it.quantity) > 0);
            const ps = Number(order.payment_status);
            const returning = !!(order.is_refund || order.is_change || [4, 5, 6, 8].includes(ps));
            const gone = returning ? list.filter((it) => Number(it.quantity) <= 0) : [];
            const shown = live.length ? live.concat(gone) : list;
            return shown.map((it) => {
                const pv = it.productVariant || {};
                const { main, other } = this.chars(it);
                const size = (other && other.value) || '';
                const color = this.nice((main && main.value) || '');
                const qty = Number(it.quantity) || 0;
                const back = returning && qty <= 0;
                const backTx = back ? (order.is_change ? t.i_swapped : ((ps === 6 || ps === 8) ? t.i_back_wait : t.i_back_done)) : '';
                return {
                    id: it.id, name: this.itemName(it), size, color, qty, back,
                    price: Number(it.payed_price) ? this.money({ amount: Number(it.payed_price), currency: this.currencyOf(order) }) : '',
                    backLabel: back ? (order.is_change ? t.i_swapped_s : t.i_back_s) : '',
                    productId: pv.product_id || (pv.product && pv.product.id) || null,
                    pv: it.product_variant_id,
                    url: it.url || null,
                    img: (pv.productImages && pv.productImages[0]) || '',
                    meta: [size, color, qty > 0 ? qty + ' ' + this.t('pcs', 'pcs') : '', backTx].filter(Boolean).join(' · '),
                };
            });
        },
        openProduct(it) {
            if (it && it.url) location.href = it.url;
        },
        parse(v) { const t = Date.parse(String(v || '').replace(' ', 'T')); return isNaN(t) ? null : new Date(t); },
        // the year only when it is not this year
        fmt(d) {
            const opt = { month: 'short', day: 'numeric' };
            if (d.getFullYear() !== new Date().getFullYear()) opt.year = 'numeric';
            try { return new Intl.DateTimeFormat(this.lan, opt).format(d); } catch (e) { return ''; }
        },
        orderDate(order) { return this.parse(order && (order.created_at_iso || order.created_at)); },
        // under the progress steps: ordered · packed · posted · delivered (the outbound delivery)
        dates(order) {
            const od = this.orderDate(order);
            const del = (order.orderDeliveries || []).slice().sort((a, b) => Number(a.id) - Number(b.id))[0] || {};
            const d = (v) => { const x = this.parse(v); return x ? this.fmt(x) : ''; };
            return [od ? this.fmt(od) : String(order.created_at || ''), d(del.packaged_at), d(del.posted_at || del.tracking_code_created_at), d(del.delivered_at)];
        },
        // the card's date, always with the year
        date(order) {
            const d = this.orderDate(order);
            if (!d) return String(order.created_at || '');
            try { return new Intl.DateTimeFormat(this.lan, { year: 'numeric', month: 'short', day: 'numeric' }).format(d); } catch (e) { return String(order.created_at || ''); }
        },

        /* ── purchased products (from the loaded orders, per variant, by year) ── */
        swatchOf(c) {
            if (!c) return { key: 'other', name: '', swatch: '' };
            const hex = String(c.hex || '').replace(/^#/, '');
            return { key: String(c.value_id), name: this.nice(c.value), swatch: /^[0-9a-f]{3,8}$/i.test(hex) ? '#' + hex : '' };
        },
        buildCollection(list) {
            const t = this.texts;
            const map = new Map();
            list.forEach((o) => {
                const s = OrderRules.state(o);
                // cancelled, failed, not finished or not received: never reached the customer
                if (s.stopped) return;
                const when = this.orderDate(o);
                const returning = s.returning || !!o.is_change;
                (o.orderItems || []).forEach((it) => {
                    const qty = Number(it.quantity) || 0;
                    const back = returning && qty <= 0;
                    if (qty <= 0 && !back) return; // removed before dispatch
                    const pv = it.productVariant || {};
                    const { main, other, orig } = this.chars(it);
                    const key = String(it.product_variant_id);
                    const e = map.get(key) || {
                        pv: it.product_variant_id,
                        name: this.itemName(it),
                        size: (other && other.value) || '',
                        color: this.swatchOf(main),
                        img: (pv.productImages && pv.productImages[0]) || '',
                        url: it.url || null,
                        sku: pv.sku || '', serial: (pv.product && pv.product.serial) || '',
                        productId: pv.product_id || (pv.product && pv.product.id) || null,
                        unit: Number(it.payed_price) || 0, cur: this.currencyOf(o),
                        available: !orig || (orig.available !== false && (orig.stock === undefined || orig.stock > 0)),
                        times: 0, kept: 0, last: null, orderNo: '',
                    };
                    if (qty > 0) { e.times += 1; e.kept += qty; }
                    if (!e.last || (when && when > e.last)) { e.last = when; e.orderNo = o.pretty_id; }
                    map.set(key, e);
                });
            });
            const all = [...map.values()].map((e) => Object.assign(e, {
                back: e.kept === 0,
                again: e.times > 1, againTx: String(t.c_again || ':n×').replace(':n', e.times),
                date: e.last ? this.fmt(e.last) : '', year: e.last ? e.last.getFullYear() : 0,
                price: e.unit ? this.money({ amount: e.unit, currency: e.cur }) : '',
                meta: [e.size, e.color.name, e.unit ? this.money({ amount: e.unit, currency: e.cur }) : ''].filter(Boolean).join(' · '),
            })).sort((a, b) => (b.last || 0) - (a.last || 0));
            const kept = all.filter((e) => !e.back);
            const base = this.collBack ? all : kept;
            const shown = base.filter((e) => this.collFilter === 'all' || e.color.key === this.collFilter);
            const colors = [];
            base.forEach((e) => { if (!colors.some((c) => c.key === e.color.key)) colors.push({ key: e.color.key, name: e.color.name, swatch: e.color.swatch }); });
            const filters = [{ key: 'all', name: t.c_all || 'All', n: base.length }]
                .concat(colors.map((c) => Object.assign(c, { n: base.filter((e) => e.color.key === c.key).length })));
            const years = [];
            shown.forEach((e) => { let y = years.find((x) => x.y === e.year); if (!y) { y = { y: e.year, list: [] }; years.push(y); } y.list.push(e); });
            const yrs = kept.map((e) => e.year).filter(Boolean);
            return {
                total: kept.length, colorsN: new Set(kept.map((e) => e.color.key)).size, since: yrs.length ? Math.min(...yrs) : '',
                filters, years, sel: all.find((e) => String(e.pv) === String(this.collSel)) || null,
                hasBack: all.some((e) => e.back), empty: all.length === 0,
            };
        },
        toggleColl(e) { this.collSel = String(this.collSel) === String(e.pv) ? null : e.pv; },
        // back to the header cart; bought in another currency → the product page, at today's price
        reorder(e) {
            const hc = window.HEADERCART && HEADERCART.vue;
            // eslint-disable-next-line no-undef -- the engine's page global (the header cart uses it too)
            const shopCur = (typeof currency === 'string' && currency) || (typeof window.currency === 'string' && window.currency) || '';
            if (!hc || !hc.addItem || !e || (shopCur && e.cur && shopCur !== e.cur)) return this.openProduct(e);
            hc.addItem(e.pv, e.unit, 1, e.size || null, e.name, e.img, e.color.name, e.sku, e.serial);
        },

        // page 1 replaces the list (also after a change in the order manage dialog), later pages are appended
        async fetchOrders(page = 1){
            let token = Cookies.getItem('login_token');
            if(!token){
                location.href = (window.SHOP_BASE || "") + "/login";
                return false;
            }
            if (page > 1) this.loadingMore = true;
            try {
                const response = await fetch(`/api/orders`, {
                    method: "POST",
                    credentials: "same-origin",
                    headers: http.headers(),
                    body: JSON.stringify({
                        token: token,
                        page: page,
                        all: 1,
                    }),
                });

                if (response.ok) {
                    const orders = await response.json();
                    const list = this.transformOrders(orders.data.all_orders);
                    if (page > 1 && this.orders) {
                        const known = new Set(this.orders.data.map((o) => String(o.id)));
                        this.orders = { ...list, data: this.orders.data.concat(list.data.filter((o) => !known.has(String(o.id)))) };
                    } else {
                        this.orders = list;
                        this.limit = this.page;
                    }
                    const current = Number(orders.data.all_orders.current_page) || page;
                    this.nextPage = orders.data.all_orders.next_page_url ? current + 1 : null;
                    this.loadingMore = false;
                    return true;
                }
                this.loadingMore = false;

                if (page === 1) this.ordersError = `Unable to load orders (${response.status})`;
                if (response.status === 401 || response.status === 403) {
                    location.href = (window.SHOP_BASE || "") + "/login";
                }
            } catch (error) {
                console.error('Unable to load orders', error);
                if (page === 1) this.ordersError = 'Unable to load orders';
            }
            this.loadingMore = false;
            return false;

        },
        transformOrders(orders){
            const transOrders = orders.data.map(item => this.normaliseOrder(item));

            return {...orders,data:transOrders}

        },
        normaliseOrder(order){
            const productTranslations = order.order_items_sum_quantity === 1
                ? order.orderItems?.[0]?.productVariant?.product?.product_translations ?? []
                : [];

            const productTranslation = productTranslations.find(
                translation => translation.language_code === this.lan
            );

            return {
                ...order,
                open: false,
                // the open item on the card (-1: none)
                _pi: -1,
                productTranslations,
                status_label: this.getOrderStatus(order),
                status_tone: this.getOrderTone(order),
                status_step: this.getOrderStep(order),
                product_name: productTranslation?.name ?? null,
                item_count: order.order_items_sum_quantity,
                products: this.getOrderProducts(order),
                details: this.getOrderDetails(order)
            };

        },
        getOrderProducts(order){
            return (order.orderItems ?? []).map(item => ({
                ...item.productVariant?.product,
                id: item.id,
                quantity: item.quantity,
                characteristics: [item.productVariant?.caracteristics_1, item.productVariant?.caracteristics_2].filter(Boolean).join(' · '),
                name: item.productVariant?.product?.product_translations?.find(
                    translation => translation.language_code === this.lan
                )?.name ?? null,
                main_image: item.productVariant?.productImages?.[0] ?? null
            }));

        },
        getOrderDetails(order){
            const address = order.delivery_address;
            const addressLabel = address
                ? [address.post_code, address.city, address.street, address.house].filter(Boolean).join(' ')
                : null;

            return {
                address: addressLabel,
                delivery: order.last_delivery_method_name,
                tracking: order.last_public_tracking_code,
                payment: order.payment_method
            };

        },
        // the customer order list has the after-sale flags on the order itself
        // (is_refund, is_change, is_deny, is_refused); after_status is the older shape
        afterStatus(order){
            const src = order.after_status ?? order;
            return {
                is_change: !!src.is_change,
                is_refund: !!src.is_refund,
                is_deny: !!src.is_deny,
                is_refused: !!src.is_refused,
            };
        },
        isDelivered(order){
            // DeliveryStatusesEnum: DELIVERED = 5, FINISHED = 6
            return [5, 6].includes(Number(order.delivered_status));
        },
        isPaymentFailed(order){
            // PaymentStatusesEnum::FAILED = 7 (order status 3 is also set by a cancellation)
            return Number(order.payment_status) === 7;
        },
        getOrderTone(order){
            const after = this.afterStatus(order);
            if(after.is_refund || after.is_deny || after.is_refused || this.isPaymentFailed(order)){
                return 'alert';
            }
            if(this.isDelivered(order)){
                return 'done';
            }
            return 'work';

        },
        getOrderStep(order){
            // collected_quantity is the warehouse picking, not the customer receiving it
            if(this.isDelivered(order)){
                return 4;
            }
            if(Number(order.order_items_sum_sent_out_quantity) > 0){
                return 3;
            }
            if(Number(order.order_items_sum_collected_quantity) > 0 || Number(order.order_items_sum_need_to_sent_quantity) < Number(order.order_items_sum_quantity)){
                return 2;
            }
            return 1;

        },
        toggleOrder(order){
            order.open = !order.open;

        },
        copyOrderNumber(orderNumber){
            if(navigator.clipboard){
                navigator.clipboard.writeText(orderNumber);
            }

        },
        getOrderStatus(order){
            const after = this.afterStatus(order);
            if(after.is_change){
                return lang.statuses.changed;
            }
            if(after.is_refund){
                return lang.statuses.refunded;
            }
            if(after.is_refused){
                return lang.statuses.refused ?? lang.statuses.deleted;
            }
            if(after.is_deny){
                return lang.statuses.deleted;
            }
            if(this.isPaymentFailed(order)){
                return lang.statuses.payment_failed ?? lang.statuses.not_finished;
            }
            if(this.isDelivered(order)){
                return lang.statuses.delivered;
            }
            if(Number(order.status) === -1){
                return lang.statuses.not_finished;
            }
            return lang.statuses.under_process;

        }

    },
    created() {
        // let token = Cookies.getItem("login_token");
        // if (!token) {
        //     window.location.pathname = "/login";
        // }
    },
};

// Global orders object
const ORDERS = {
    vue: null,
    load() {
        if (document.getElementById("orders") !== null) {
            this.vue = Vue.createApp(Orders)
            this.vue.mount("#orders");
            forms.load();
        }
    },
};
window.ORDERS = ORDERS;

window.addEventListener("DOMContentLoaded", () => {
    setTimeout(() => ORDERS.load(),0);
});

if (document.readyState !== "loading") {
    setTimeout(() => ORDERS.load(),0);
}
